import { Field, FieldProps, Formik, FormikHelpers, FormikProps } from 'formik';
import { useRef, useState } from 'react';
import * as yup from 'yup';
import { TextField } from '~/components/form/TextField';
import { Label } from '../ui/Label';
import { PopupDialogNew } from '../ui/PopupDialogNew';
import { CategoryCreateDialog } from './CategoryCreateDialog';
import { CategorySelect } from './CategorySelect';
import { ColorPicker } from './ColorPicker';
import { TagBlock } from './TagsBuilder';
import type { Tag } from './tags';

export type TagFormSubmitResult = {
  success: boolean;
  error?: string;
  field?: string;
};

type Props = {
  isOpen: boolean;
  initialValues?: Tag;
  tags: Tag[];
  onClose: () => void;
  onSubmit: (values: Tag) => Promise<TagFormSubmitResult>;
  onRenameCategory?: (category: string) => void;
  onDeleteCategory?: (category: string) => void;
};

export function TagForm({
  isOpen = false,
  tags,
  initialValues = {
    id: '',
    entityType: '',
    name: '',
    category: null,
    description: null,
    colour: '81848F',
  },
  onClose,
  onSubmit,
  onRenameCategory,
  onDeleteCategory,
}: Props) {
  const isEdit = !!initialValues.id;
  const [createCategoryInitialName, setCreateCategoryInitialName] = useState<
    string | undefined
  >();
  const [isCreateCategoryOpen, setIsCreateCategoryOpen] = useState(false);
  const formikRef = useRef<FormikProps<Tag> | null>(null);

  const tagCategories = () => {
    const categories = new Set<string>();

    tags.forEach((tag) => {
      if (tag.category) {
        categories.add(tag.category);
      }
    });

    return Array.from(categories);
  };

  const handleCreateCategory = (initialName?: string) => {
    setCreateCategoryInitialName(initialName);
    setIsCreateCategoryOpen(true);
  };

  const handleCreateCategorySubmit = (categoryName: string) => {
    formikRef.current?.setFieldValue('category', categoryName);
  };

  return (
    <>
      <CategoryCreateDialog
        isOpen={isCreateCategoryOpen}
        initialName={createCategoryInitialName}
        existingCategories={tagCategories()}
        onClose={() => setIsCreateCategoryOpen(false)}
        onSubmit={handleCreateCategorySubmit}
      />

      <PopupDialogNew
        isOpen={isOpen}
        onClose={onClose}
        title={isEdit ? 'Edit Tag' : 'Create New Tag'}
        stacked={isCreateCategoryOpen}
      >
        <Formik
          initialValues={initialValues}
          onSubmit={async (values: Tag, helpers: FormikHelpers<Tag>) => {
            const result = await onSubmit(values);

            if (result.success) {
              onClose();
            } else {
              // Handle failure: set field-level error if present
              if (result.error) {
                // Set field-level error if field is specified, otherwise set on 'name' field
                const fieldName = result.field || 'name';
                // Touch the field so the error displays
                helpers.setFieldTouched(fieldName, true, false);
                helpers.setFieldError(fieldName, result.error);
              }
              // Always stop submitting on any non-success branch
              helpers.setSubmitting(false);
            }
          }}
          validationSchema={yup.object().shape({
            name: yup.string().required('Required'),
          })}
          innerRef={formikRef}
        >
          {(props) => {
            const { values, isSubmitting } = props;

            return (
              <div className='overflow-y-auto'>
                <div className='mx-4 mb-1'>
                  {values.name && (
                    <div className='my-3 flex flex-col gap-1 py-3'>
                      <Label>Tag Preview</Label>
                      <TagBlock
                        hexColor={values.colour}
                        tagName={values.name}
                      />
                    </div>
                  )}
                  <TextField
                    className='my-5'
                    name='name'
                    label='Tag Name'
                    autoFocus
                    required
                  />
                  <Field name='category'>
                    {({ field, form }: FieldProps<string | null, Tag>) => (
                      <CategorySelect
                        label='Tag Category'
                        items={tagCategories()}
                        value={field.value}
                        onChange={(value) =>
                          form.setFieldValue('category', value)
                        }
                        onCreateCategory={handleCreateCategory}
                        onRenameCategory={onRenameCategory}
                        onDeleteCategory={onDeleteCategory}
                        required
                      />
                    )}
                  </Field>
                  <TextField
                    className='my-5'
                    name='description'
                    label='Description'
                  />
                  <Field name='colour'>
                    {({ field }: FieldProps<string, Tag>) => {
                      const { value, name } = field;

                      return (
                        <ColorPicker
                          label={'Color'}
                          hexValue={value}
                          onChange={(hexValue) =>
                            props.setFieldValue(name, hexValue.substring(1))
                          }
                          randomizeColor={!isEdit}
                        />
                      );
                    }}
                  </Field>
                </div>

                <div className='flex justify-end px-4 py-6'>
                  <button
                    className='w-fit px-7 py-1.5 text-sm font-thin uppercase text-grey-40 hover:text-brand disabled:opacity-50'
                    onClick={onClose}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type='button'
                    className='w-fit rounded-lg bg-brand px-3 py-2 text-sm font-thin uppercase text-white hover:bg-brand-dark disabled:opacity-50 disabled:hover:bg-brand'
                    onClick={() => props.handleSubmit()}
                    disabled={isSubmitting}
                  >
                    {isSubmitting
                      ? 'Saving...'
                      : isEdit
                      ? 'Save Tag'
                      : 'Create Tag'}
                  </button>
                </div>
              </div>
            );
          }}
        </Formik>
      </PopupDialogNew>
    </>
  );
}
